// @flow

import React, { PureComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import moment from 'moment';
import styled from 'styled-components';
import type { RouterHistory } from 'react-router';
import { i18n, getCompanyCurrency } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import DeleteInvoiceMutation from 'main-app/mutations/DeleteInvoice';
import Table from 'shared/components/common/Table';
import InvoiceStatusPill from 'shared/components/common/Pill/InvoiceStatusPill';

const Wrapper = styled.div`
  padding: 0 24px;
`;

type Props = {
  history: RouterHistory,
  invoiceEdges: Array<Object>,
  deleteEnabled: boolean,
  editEnabled: boolean,
  onCreateNew: () => void,
};

const formatCurrency = (value: ?number, currency: string = getCompanyCurrency()) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

class InvoicesTable extends PureComponent<Props> {
  handleRowClick = (rowData: Object) => {
    const { history } = this.props;
    history.push(`/invoice/${rowData.node.id}`);
  };

  handleDelete = (invoice: Object) => {
    DeleteInvoiceMutation.commit({
      variables: { input: { id: invoice.id } },
      onCompleted: () => {
        Actions.alertNotification(i18n.t('Invoice deleted successfully.'), 'success');
      },
      onError: () => {
        Actions.alertNotification(i18n.t('Error deleting invoice.'), 'error');
      },
    });
  };

  getColumns = () => {
    const columns = [
      {
        Header: i18n.t('Invoice #'),
        accessor: 'node.invoiceNumber',
        width: 130,
      },
      {
        Header: i18n.t('Order #'),
        accessor: 'node.order.orderNumber',
        width: 120,
      },
      {
        Header: i18n.t('Customer'),
        accessor: 'node.customer.name',
        minWidth: 160,
      },
      {
        Header: i18n.t('Status'),
        accessor: 'node.status',
        width: 140,
        Cell: ({ value }: *) => <InvoiceStatusPill status={value} />,
      },
      {
        Header: i18n.t('Total'),
        accessor: 'node.totalAmount',
        width: 120,
        Cell: ({ original }: *) =>
          formatCurrency(original.node.totalAmount, original.node.currency),
      },
      {
        Header: i18n.t('Amount Due'),
        accessor: 'node.amountDue',
        width: 120,
        Cell: ({ original }: *) =>
          formatCurrency(original.node.amountDue, original.node.currency),
      },
      {
        Header: i18n.t('Issue Date'),
        accessor: 'node.issueDate',
        width: 120,
        Cell: ({ value }: *) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
      },
      {
        Header: i18n.t('Due Date'),
        accessor: 'node.dueDate',
        width: 120,
        Cell: ({ value }: *) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
      },
      {
        Header: i18n.t('Created'),
        accessor: 'node.createdAt',
        width: 130,
        Cell: ({ value }: *) => moment(value).format('MM/DD/YYYY'),
      },
    ];

    return columns;
  };

  render() {
    const { invoiceEdges, deleteEnabled } = this.props;

    if (!invoiceEdges || invoiceEdges.length === 0) {
      return null;
    }

    return (
      <Wrapper>
        <Table
          data={invoiceEdges}
          columns={this.getColumns()}
          onRowClick={this.handleRowClick}
          contextMenuItems={
            deleteEnabled
              ? [
                  {
                    label: i18n.t('Delete'),
                    onClick: (row: Object) => this.handleDelete(row.node),
                  },
                ]
              : []
          }
        />
      </Wrapper>
    );
  }
}

export default createFragmentContainer(InvoicesTable, {
  invoiceEdges: graphql`
    fragment InvoicesTable_invoiceEdges on InvoiceEdge @relay(plural: true) {
      node {
        id
        invoiceNumber
        status
        currency
        totalAmount
        amountDue
        issueDate
        dueDate
        notes
        order {
          id
          orderNumber
        }
        customer {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  `,
});
